import { useEffect, useState } from 'react'

/**
 * Custom hook for media queries with proper SSR support.
 *
 * @param query - The media query to evaluate
 * @param defaultState - The default state to use during SSR (default: false)
 * @returns A boolean indicating if the media query matches
 */
export function useMedia(
  query: string,
  defaultState: boolean = false
): boolean {
  // During SSR, always return the default state to prevent hydration mismatches
  const [state, setState] = useState<boolean>(defaultState)

  useEffect(() => {
    // Only run on client-side
    if (typeof window === 'undefined') return

    // Create and setup media query list
    const mediaQueryList = window.matchMedia(query)

    // Update the state to match the current value
    const updateState = () => setState(mediaQueryList.matches)

    // Set initial state
    updateState()

    // Add listener for changes
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', updateState)
    } else {
      // Fallback for older browsers
      mediaQueryList.addListener(updateState)
    }

    // Clean up
    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', updateState)
      } else {
        // Fallback for older browsers
        mediaQueryList.removeListener(updateState)
      }
    }
  }, [query])

  return state
}

export default useMedia
